import { Box } from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Pacote } from "../../types";
import { CSelect, CUpload, VDUploadRef } from "../inputs";
import Input from "../inputs/Input.component";

interface FormPacotesProps {
  initialValues?: Pacote | null;
  onSubmit: (values: Pacote) => Promise<unknown>;
  loading?: boolean;
}

export interface FormPacoteRef {
  submit: () => Promise<void>;
  getFiles: () => File[] | undefined;
  getInitialFiles: () => string[] | undefined;
  getDeleteFiles: () => string[] | undefined;
}

export const FormPacotes = forwardRef<FormPacoteRef, FormPacotesProps>(
  (props, ref) => {
    const { register, handleSubmit, setValue, watch, reset } =
      useForm<Pacote>();
    const reactQuillRef = useRef<ReactQuill | null>(null);
    const uploadRef = useRef<VDUploadRef>(null);

    useEffect(() => {
      if (props.initialValues) {
        reset(props.initialValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialValues]);

    const onSubmit: SubmitHandler<Pacote> = (values) => {
      props.onSubmit(values);
    };

    // Exposing the submit function via ref
    useImperativeHandle(ref, () => ({
      submit: handleSubmit(onSubmit),
      getFiles: () => uploadRef?.current?.getFiles(),
      getInitialFiles: () => uploadRef?.current?.getInitialFiles(),
      getDeleteFiles: () => uploadRef?.current?.getDeleteFiles(),
    }));

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Input label="Titulo" {...register("title")} loading={props.loading} />
        <Input label="Local" {...register("local")} loading={props.loading} />
        <Box sx={{ display: "flex", gap: "20px" }}>
          <Input
            label="Data de Partida"
            type="date"
            InputLabelProps={{ shrink: true }}
            {...register("data_partida")}
            loading={props.loading}
          />
          <Input
            label="Data de Retorno"
            type="date"
            InputLabelProps={{ shrink: true }}
            {...register("data_retorno")}
            loading={props.loading}
          />
        </Box>
        <Input
          label="Tipo do Transporte"
          {...register("tipo_transporte")}
          loading={props.loading}
        />
        <Input
          suffix="R$"
          label="Preço"
          {...register("preco")}
          loading={props.loading}
        />
        <CSelect
          label="Categoria"
          data={["Pacote", "Caravana", "Excursão"]}
          {...register("tag")}
        />
        <ReactQuill
          modules={{
            toolbar: {
              container: [
                [{ font: [] }],
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ["bold", "italic", "underline", "strike"],
                ["blockquote", "code-block"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ script: "sub" }, { script: "super" }],
                [{ indent: "-1" }, { indent: "+1" }],
                [{ direction: "rtl" }],
                [{ color: [] }, { background: [] }],
                [{ align: [] }],
                ["link", "image", "video"],
                ["clean"],
              ],
            },
          }}
          ref={reactQuillRef}
          theme="snow"
          value={watch("descricao")}
          onChange={(e) => setValue("descricao", e)}
          placeholder="Digite sua descrição aqui..."
        />
        <CUpload ref={uploadRef} defaultValue={watch("imagesUrl")} />
      </Box>
    );
  }
);
